import {
  AILatencyMetricsSchema,
  AIQuestionAnswerSchema,
  AIStreamingStatusSchema,
  CalendarSyncResultSchema,
  CallQuestionSchema,
  PartialAIResponseSchema,
  PlaybookProgressSchema,
  PlaybookScriptItemSchema,
  PlaybookSectionSchema,
  RecallBotSchema,
  RecallMeetingParticipantSchema,
  StatusChangeSchema,
} from "@frontierx/types";
import { z } from "zod";

import { TranscriptUpdateSchema } from "./TranscriptUpdate";

export const ServerMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("FULL_TRANSCRIPT"),
    payload: z.array(TranscriptUpdateSchema),
  }),
  z.object({
    type: z.literal("AI_HISTORY"),
    payload: z.array(AIQuestionAnswerSchema),
  }),
  z.object({
    type: z.literal("TRANSCRIPT_UPDATE"),
    payload: TranscriptUpdateSchema,
  }),
  z.object({
    type: z.literal("STATUS_UPDATE"),
    payload: z.object({
      status: StatusChangeSchema,
    }),
  }),
  z.object({
    type: z.literal("STATUS_CHANGE"),
    payload: StatusChangeSchema,
  }),
  z.object({
    type: z.literal("PARTICIPANT_JOIN"),
    payload: z.object({
      participant: RecallMeetingParticipantSchema,
    }),
  }),
  z.object({
    type: z.literal("PARTICIPANT_LEAVE"),
    payload: z.object({
      participant: RecallMeetingParticipantSchema,
    }),
  }),
  z.object({
    type: z.literal("PARTICIPANT_WAITING"),
    payload: z.object({
      participant: RecallMeetingParticipantSchema,
    }),
  }),
  z.object({
    type: z.literal("AI_HELP_STREAM_INFO"),
    payload: AIStreamingStatusSchema,
  }),
  z.object({
    type: z.literal("AI_HELP_STREAM"),
    payload: PartialAIResponseSchema,
  }),
  z.object({
    type: z.literal("AI_HELP_STREAM_END"),
    payload: PartialAIResponseSchema,
  }),
  z.object({
    type: z.literal("AI_LATENCY_METRICS"),
    payload: AILatencyMetricsSchema,
  }),
  z.object({
    type: z.literal("AI_ERROR"),
    payload: z.object({
      message: z.string(),
    }),
  }),
  z.object({
    type: z.literal("ERROR"),
    payload: z.object({
      message: z.string(),
    }),
  }),
  z.object({
    type: z.literal("BOT_INFO"),
    payload: RecallBotSchema,
  }),
  z.object({
    type: z.literal("CURRENT_PLAYBOOK_SECTION"),
    payload: z.string(),
  }),
  z.object({
    type: z.literal("PLAYBOOK_SECTION_UPDATE"),
    payload: PlaybookSectionSchema,
  }),
  z.object({
    type: z.literal("PLAYBOOK_PROGRESS"),
    payload: PlaybookProgressSchema,
  }),
  z.object({
    type: z.literal("CURRENT_SCRIPT_ITEM"),
    payload: PlaybookScriptItemSchema,
  }),
  z.object({
    type: z.literal("PLAYBOOK_COMPLETE"),
  }),
  z.object({
    type: z.literal("SCRIPT_ITEM_COMPLETED"),
    payload: z.object({
      id: z.string(),
      text: z.string(),
      completionMethod: z.string(),
      timestamp: z.number(),
    }),
  }),
  z.object({
    type: z.literal("CALL_QUESTIONS_UPDATE"),
    payload: z.array(CallQuestionSchema),
  }),
  z.object({
    type: z.literal("MESSAGE_LATENCY"),
    payload: z.number(),
  }),
  z.object({
    type: z.literal("PROCESS_SCRIPT"),
    payload: z.boolean(),
  }),
  z.object({
    type: z.literal("PARTICIPANTS"),
    payload: z.array(RecallMeetingParticipantSchema),
  }),

  // Include client message types that might be echoed back to the client
  z.object({
    type: z.literal("INITIALIZE_FEEDBACK"),
    payload: z.object({
      needsIntroCard: z.boolean(),
      hostName: z.string(),
      companyName: z.string(),
      timestamp: z.number(),
      customerPersona: z.any().optional(),
    }),
  }),
  z.object({
    type: z.literal("REJOIN_CALL"),
    payload: z.object({
      needsIntroCard: z.boolean(),
      hostName: z.string(),
      companyName: z.string(),
      timestamp: z.number(),
    }),
  }),
  z.object({
    type: z.literal("PARTICIPANT_ROLES_UPDATED"),
    payload: z.array(
      z.object({
        recallParticipantId: z.string(),
        role: z.enum(["salesperson", "client", "observer"]),
        displayName: z.string(),
        isCurrentUser: z.boolean().optional(),
        assignedBy: z.string().optional(),
      }),
    ),
  }),
  z.object({
    type: z.literal("PARTICIPANT_ROLES_RESPONSE"),
    payload: z.object({
      assignments: z.array(
        z.object({
          recallParticipantId: z.string(),
          role: z.enum(["salesperson", "client", "observer"]),
          displayName: z.string(),
          isCurrentUser: z.boolean().optional(),
          assignedBy: z.string().optional(),
        }),
      ),
      participants: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          role: z.enum(["salesperson", "client", "observer"]),
          isHost: z.boolean(),
          hasExplicitAssignment: z.boolean(),
          assignedAt: z.string().optional(),
        }),
      ),
    }),
  }),
  z.object({
    type: z.literal("SCRIPT_DETECTION_LOG"),
    payload: z.object({
      timestamp: z.number(),
      scriptItemId: z.string(),
      scriptItemText: z.string(),
      triggeringText: z.string(),
      checksPerformed: z.array(
        z.object({
          method: z.string(),
          score: z.number(),
          partialThreshold: z.number(),
          fullThreshold: z.number(),
        }),
      ),
      decision: z.enum(["completed", "partial", "no_match"]),
      reason: z.string(),
      similarity: z.number().optional(),
    }),
  }),
  z.object({
    type: z.literal("SCRIPT_SELECTION_REQUIRED"),
    payload: z.object({
      message: z.string(),
      orgId: z.string(),
    }),
  }),
  z.object({
    type: z.literal("CALENDAR_SYNC_COMPLETE"),
    payload: z.object({
      userId: z.string(),
      orgId: z.string(),
      sessionId: z.string().optional(),
      syncedProviders: z.array(z.string()).optional(),
      totalEvents: z.number(),
      timestamp: z.string(),
      results: z.array(CalendarSyncResultSchema).optional(),
    }),
  }),
  z.object({
    type: z.literal("CALENDAR_REMOVED"),
    payload: z.object({
      calendarId: z.string(),
      userId: z.string(),
      orgId: z.string(),
      timestamp: z.string(),
    }),
  }),
]);

export type ServerMessage = z.infer<typeof ServerMessageSchema>;
